import { lazy, Suspense, useEffect, useRef, useState } from "react";

const LazyBackgroundCard = lazy(() => import("./LazyBackgroundCard"));

type DeferredDeveloperCardProps = {
	avatar: string;
	name: string;
};

const DeveloperCardFallback = ({ name }: Pick<DeferredDeveloperCardProps, "name">) => (
	<div
		aria-hidden="true"
		className="flex aspect-[4/5] w-full items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[#1a181c]"
	>
		<span className="text-5xl font-bold text-white/15 sm:text-6xl">
			{name.charAt(0).toUpperCase()}
		</span>
	</div>
);

const DeferredDeveloperCard = ({ avatar, name }: DeferredDeveloperCardProps) => {
	const hostRef = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const host = hostRef.current;
		if (!host) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry?.isIntersecting) return;
				setVisible(true);
				observer.disconnect();
			},
			{ rootMargin: "300px 0px" },
		);

		observer.observe(host);
		return () => observer.disconnect();
	}, []);

	return (
		<div ref={hostRef}>
			{visible ? (
				<Suspense fallback={<DeveloperCardFallback name={name} />}>
					<LazyBackgroundCard avatar={avatar} name={name} />
				</Suspense>
			) : (
				<DeveloperCardFallback name={name} />
			)}
		</div>
	);
};

export default DeferredDeveloperCard;
